import React, { createContext, useEffect, useState } from 'react'
import ReactDOM from 'react-dom/client'
import './index.css'
import reportWebVitals from './reportWebVitals'
import { loginRequest, msalConfig } from './authConfig'
import { MsalProvider, useIsAuthenticated, useMsal } from '@azure/msal-react'
import {
  BrowserAuthError,
  BrowserAuthErrorCodes,
  InteractionRequiredAuthError,
  PublicClientApplication,
} from '@azure/msal-browser'
import { createBrowserRouter, RouterProvider } from 'react-router-dom'
import routes from './routes'
import axios from 'axios'
import { getCustomers, getOperations, getProfile, getSalesReps } from './helper'
import { customerType, salesRepType, userType } from './types'
import { createTheme, ThemeProvider } from '@mui/material'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { reactPlugin } from '@datadog/browser-rum-react'
import { datadogRum } from '@datadog/browser-rum'

dayjs.extend(utc)

axios.defaults.baseURL = process.env.REACT_APP_BACKEND_URL

const msalInstance = new PublicClientApplication(msalConfig)
const router = createBrowserRouter(routes)

axios.interceptors.request.use(
  async config => {
    try {
      const response = await msalInstance.acquireTokenSilent({
        scopes: [`api://${process.env.REACT_APP_CLIENT_ID}/access`],
        account: msalInstance.getActiveAccount() ?? undefined,
        forceRefresh: true,
      })

      config.headers.Authorization = response.accessToken
      return config
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        const response = await msalInstance.acquireTokenPopup({
          scopes: [`api://${process.env.REACT_APP_CLIENT_ID}/access`],
        })

        config.headers.Authorization = response.accessToken
        return config
      } else if (error instanceof BrowserAuthError && error.errorCode === BrowserAuthErrorCodes.monitorWindowTimeout) {
        msalInstance.acquireTokenRedirect({
          scopes: [`api://${process.env.REACT_APP_CLIENT_ID}/access`],
        })
        return config
      } else {
        console.error(error)
        throw error
      }
    }
  },
  error => {
    return Promise.reject(error)
  },
)

datadogRum.init({
  applicationId: '1022a9e8-c4f1-46de-905e-ff769ae4dfdd',
  clientToken: 'pubeae429124d719d78576e7caa101d1686',
  site: 'us5.datadoghq.com',
  service: 'salestrack',
  env: process.env.REACT_APP_DATADOG_ENV ?? 'dev',
  version: '1.0.0',
  sessionSampleRate: 100,
  sessionReplaySampleRate: process.env.REACT_APP_DATADOG_ENV === 'prod' ? 100 : 0,
  defaultPrivacyLevel: 'allow',
  plugins: [reactPlugin({ router: true })],
  startSessionReplayRecordingManually: true,
  trackViewsManually: true,
})

export const dataContext = createContext({
  user: {
    user_id: '',
    user_type: 'Read Only',
    display_name: null,
    email: '',
    active_flag: false,
  } as userType,
  operations: [] as userType[],
  salesReps: [] as salesRepType[],
  customers: [] as customerType[],
})

const palette = createTheme({
  palette: {
    primary: {
      main: '#32578b',
      contrastText: '#FFFFFF',
    },
  },
})

const theme = createTheme({
  cssVariables: true,
  typography: {
    fontFamily: 'Arial, sans-serif',
  },
  components: {
    MuiAlert: {
      styleOverrides: {
        root: {
          variants: [
            {
              props: { severity: 'error' },
              style: {
                backgroundColor: palette.palette.error.main,
                color: palette.palette.error.contrastText,
                '& .MuiAlert-icon': {
                  color: palette.palette.error.contrastText,
                },
              },
            },
          ],
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        outlined: {
          backgroundColor: 'white',
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          tableLayout: 'fixed',
          variants: [
            {
              props: { color: 'primary' },
              style: {
                '& .MuiTableHead-root .MuiTableCell-root': {
                  backgroundColor: palette.palette.primary.main,
                  color: palette.palette.primary.contrastText,
                },
                '& .MuiTableHead-root .MuiTableSortLabel-root, & .MuiTableHead-root .MuiTableSortLabel-root svg, & .MuiTableHead-root .MuiIconButton-root':
                  {
                    color: palette.palette.primary.contrastText,
                  },
                '& .MuiTableHead-root .MuiCheckbox-root': {
                  color: palette.palette.primary.contrastText,
                },
              },
            },
            {
              props: { color: 'secondary' },
              style: {
                '& .MuiTableHead-root .MuiTableCell-root': {
                  backgroundColor: '#f5f5f5',
                },
              },
            },
          ],
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          backgroundColor: 'inherit',
          whiteSpace: 'pre-wrap !important',
          '&.MuiTableCell-stickyHeader': {
            top: '102px',
          },
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          background: 'white',
          overflowX: 'initial',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:nth-of-type(even)': {
            backgroundColor: '#ECEFF1',
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        list: {
          height: '100%',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          background: 'white',
        },
      },
    },
  },
  palette: palette.palette,
})

function App() {
  const isAuthenticated = useIsAuthenticated()
  const { instance, inProgress } = useMsal()

  const [user, setUser] = useState<userType>({
    user_id: '',
    user_type: '',
    display_name: '',
    email: '',
    active_flag: false,
  })
  const [operations, setOperations] = useState<userType[]>([])
  const [salesReps, setSalesReps] = useState<salesRepType[]>([])
  const [customers, setCustomers] = useState<customerType[]>([])

  useEffect(() => {
    if (inProgress !== 'none') {
      return
    }

    if (isAuthenticated) {
      instance.setActiveAccount(instance.getAllAccounts()[0])
      datadogRum.startView()
      datadogRum.startSessionReplayRecording()

      if (!user.email) {
        getProfile(instance.getActiveAccount()?.username ?? '').then(user => {
          setUser(user)

          datadogRum.setUser({
            id: user.email,
            email: user.email,
          })
        })
      }

      if (operations.length === 0) {
        getOperations()
          .then(ops => {
            setOperations(ops)
          })
          .catch(e => {
            console.error(e)
          })
      }

      if (salesReps.length === 0) {
        getSalesReps()
          .then(salesReps => {
            setSalesReps(salesReps)
          })
          .catch(e => {
            console.error(e)
          })
      }

      if (customers.length === 0) {
        getCustomers()
          .then(customers => {
            setCustomers(customers)
          })
          .catch(e => {
            console.error(e)
          })
      }
    } else {
      instance.loginRedirect(loginRequest).catch(e => {
        console.log(e)
      })

      instance.handleRedirectPromise().then(() => {
        if (instance.getAllAccounts().length > 0) {
          instance.setActiveAccount(instance.getAllAccounts()[0])
          getProfile(instance.getActiveAccount()?.username ?? '').then(user => {
            setUser(user)
          })
        }
      })
    }
  }, [inProgress, isAuthenticated, instance])

  return isAuthenticated ? (
    <dataContext.Provider value={{ user, operations, salesReps, customers }}>
      <RouterProvider router={router} />
    </dataContext.Provider>
  ) : (
    <div>
      <p>Please sign in</p>
    </div>
  )
}

msalInstance.initialize().then(() => {
  const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement)
  root.render(
    <React.StrictMode>
      <MsalProvider instance={msalInstance}>
        <ThemeProvider theme={theme}>
          <App />
        </ThemeProvider>
      </MsalProvider>
    </React.StrictMode>,
  )
})

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals()
